import React, { useState, useEffect } from 'react';
import { useOutletContext, useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Loader2, Plus, Trash2, Edit2, PenTool, Info, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API } from '../../config/api';
import axios from 'axios';
import ReferencesSection from '../../components/ReferencesSection';

const SYSTEM_TYPES = [
  { value: 'alphabetic', label: 'Alphabetic', desc: 'Letters represent individual sounds' },
  { value: 'syllabic', label: 'Syllabic (Syllabary)', desc: 'Characters represent syllables' },
  { value: 'morphographic', label: 'Morphographic (Logographic)', desc: 'Characters represent morphemes or words' },
];

const ALPHABETIC_SUBTYPES = [
  { value: 'alphabet', label: 'Alphabet', desc: 'Vowels and consonants are written equally (e.g., Latin, Greek, Cyrillic)' },
  { value: 'abjad', label: 'Abjad', desc: 'Consonants are primary; vowels may be omitted or marked with diacritics (e.g., Arabic, Hebrew)' },
  { value: 'abugida', label: 'Abugida', desc: 'Consonant-vowel sequences as units; vowels modify a base consonant (e.g., Devanagari, Ethiopic)' },
];

const LINE_ORIENTATIONS = [
  { value: 'ltr', label: 'Left to Right' },
  { value: 'rtl', label: 'Right to Left' },
  { value: 'ttb', label: 'Top to Bottom' },
  { value: 'btt', label: 'Bottom to Top' },
];

const getPageOrientations = (lineOri) => {
  if (lineOri === 'ltr' || lineOri === 'rtl') {
    return [{ value: 'ttb', label: 'Top to Bottom' }, { value: 'btt', label: 'Bottom to Top' }];
  }
  return [{ value: 'ltr', label: 'Left to Right' }, { value: 'rtl', label: 'Right to Left' }];
};

const WritingSystemsPage = () => {
  const { conlang, fetchWritingSystems: refreshSidebar , canEdit} = useOutletContext();
  const { conlangId } = useParams();
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSystem, setEditingSystem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('alphabetic');
  const [subtype, setSubtype] = useState('alphabet');
  const [lineOri, setLineOri] = useState('ltr');
  const [pageOri, setPageOri] = useState('ttb');

  useEffect(() => { fetchData(); }, [conlangId]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/conlangs/${conlangId}/writing-systems`, { withCredentials: true });
      setSystems(res.data);
    } catch { toast.error('Failed to load writing systems'); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setName(''); setDescription(''); setType('alphabetic'); setSubtype('alphabet');
    setLineOri('ltr'); setPageOri('ttb'); setEditingSystem(null);
  };

  const openEdit = (s) => {
    setEditingSystem(s);
    setName(s.name); setDescription(s.description || ''); setType(s.type);
    setSubtype(s.alphabetic_subtype || 'alphabet');
    setLineOri(s.line_orientation); setPageOri(s.page_orientation);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    const data = {
      name, description: description || null, type,
      alphabetic_subtype: type === 'alphabetic' ? subtype : null,
      line_orientation: lineOri, page_orientation: pageOri,
    };
    try {
      if (editingSystem) {
        const res = await axios.put(`${API}/conlangs/${conlangId}/writing-systems/${editingSystem.system_id}`, data, { withCredentials: true });
        setSystems(systems.map(s => s.system_id === editingSystem.system_id ? res.data : s));
        toast.success('Writing system updated');
        refreshSidebar?.();
      } else {
        const res = await axios.post(`${API}/conlangs/${conlangId}/writing-systems`, data, { withCredentials: true });
        setSystems([...systems, res.data]);
        toast.success('Writing system created');
        refreshSidebar?.();
      }
      setDialogOpen(false); resetForm();
    } catch { toast.error('Failed to save writing system'); }
    finally { setFormLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this writing system and all its graphemes?')) return;
    try {
      await axios.delete(`${API}/conlangs/${conlangId}/writing-systems/${id}`, { withCredentials: true });
      setSystems(systems.filter(s => s.system_id !== id));
      toast.success('Writing system deleted');
      refreshSidebar?.();
    } catch { toast.error('Failed to delete'); }
  };

  const getTypeLabel = (s) => {
    if (s.type === 'alphabetic' && s.alphabetic_subtype) {
      return ALPHABETIC_SUBTYPES.find(t => t.value === s.alphabetic_subtype)?.label || s.alphabetic_subtype;
    }
    return SYSTEM_TYPES.find(t => t.value === s.type)?.label || s.type;
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground" data-testid="writing-title">Writing Systems</h2>
          <p className="text-foreground-muted mt-1">Define scripts and writing systems for {conlang?.name}</p>
        </div>
        {canEdit && (
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-primary hover:bg-primary/90" data-testid="add-writing-system-btn">
          <Plus className="w-4 h-4 mr-2" /> New Writing System
        </Button>
        )}
      </div>

      {systems.length > 0 ? (
        <div className="space-y-4" data-testid="writing-systems-list">
          {systems.map(s => (
            <Link key={s.system_id} to={`/conlang/${conlangId}/writing/${s.system_id}`} className="block">
              <Card className="bg-background-paper border-border hover:border-primary/40 transition-colors cursor-pointer">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <PenTool className="w-5 h-5 text-primary" />
                        <h3 className="font-heading text-lg text-foreground font-semibold">{s.name}</h3>
                        <Badge variant="outline" className="text-secondary border-secondary/50">{getTypeLabel(s)}</Badge>
                      </div>
                      {s.description && <p className="text-foreground-muted text-sm mb-2">{s.description}</p>}
                      <div className="flex items-center gap-4 text-xs text-foreground-subtle">
                        <span>{s.grapheme_count} grapheme{s.grapheme_count !== 1 ? 's' : ''}</span>
                        <span>Lines: {LINE_ORIENTATIONS.find(o => o.value === s.line_orientation)?.label}</span>
                      </div>
                    </div>
                    <div className="flex gap-1" onClick={(e) => e.preventDefault()}>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.preventDefault(); openEdit(s); }} data-testid={`edit-ws-${s.system_id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.preventDefault(); handleDelete(s.system_id); }} className="text-destructive hover:text-destructive" data-testid={`delete-ws-${s.system_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <Card className="bg-background-paper border-border border-dashed">
          <CardContent className="p-12 text-center">
            <PenTool className="w-12 h-12 text-foreground-muted mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-foreground mb-2">No writing systems yet</h3>
            <p className="text-foreground-muted mb-4">Create a script for your language — an alphabet, syllabary, or logographic system.</p>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-primary hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-2" /> New Writing System
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="p-4 rounded-md bg-primary/10 border border-primary/30 flex gap-3">
        <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <div className="text-primary text-sm">
          <strong>Writing systems</strong> define how your language is written. <strong>Alphabets</strong> represent individual sounds, <strong>abjads</strong> primarily write consonants, <strong>abugidas</strong> combine consonants with vowel markers, <strong>syllabaries</strong> represent syllables, and <strong>logographic</strong> systems represent morphemes or words.
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-background-paper border-border max-w-lg max-h-[90vh] overflow-y-auto" data-testid="writing-system-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">{editingSystem ? 'Edit' : 'New'} Writing System</DialogTitle>
            <DialogDescription className="text-foreground-muted">Define the properties of this writing system</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Standard Script, Formal Glyphs" className="bg-background border-border" required data-testid="ws-name" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="How is this writing system used? Is it for everyday writing, formal texts, sacred texts?" className="bg-background border-border min-h-[60px]" data-testid="ws-description" />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={type} onValueChange={(v) => { setType(v); if (v !== 'alphabetic') setSubtype(''); }}>
                <SelectTrigger className="bg-background border-border" data-testid="ws-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-background-paper border-border">
                  {SYSTEM_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-foreground-subtle">{SYSTEM_TYPES.find(t => t.value === type)?.desc}</p>
            </div>
            {type === 'alphabetic' && (
              <div className="space-y-2">
                <Label>Alphabetic Subtype</Label>
                <Select value={subtype} onValueChange={setSubtype}>
                  <SelectTrigger className="bg-background border-border" data-testid="ws-subtype"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-background-paper border-border">
                    {ALPHABETIC_SUBTYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-foreground-subtle">{ALPHABETIC_SUBTYPES.find(t => t.value === subtype)?.desc}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Line Orientation</Label>
                <Select value={lineOri} onValueChange={(v) => {
                  setLineOri(v);
                  const opts = getPageOrientations(v);
                  setPageOri(opts[0].value);
                }}>
                  <SelectTrigger className="bg-background border-border" data-testid="ws-line-ori"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-background-paper border-border">
                    {LINE_ORIENTATIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Page Orientation</Label>
                <Select value={pageOri} onValueChange={setPageOri}>
                  <SelectTrigger className="bg-background border-border" data-testid="ws-page-ori"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-background-paper border-border">
                    {getPageOrientations(lineOri).map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
              <Button type="submit" disabled={formLoading || !name} data-testid="submit-ws">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingSystem ? 'Save' : 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ReferencesSection section="writing" />
    </div>
  );
};

export default WritingSystemsPage;
